/* eslint-disable */
"use client";
import { CHAIN_TYPE } from "@/constants";
import { ContextProps, UserContextType } from "@/constants/interfaces";
import UseGetApi from "@/hooks/useFetch";
import axios from "axios";
import { usePathname } from "next/navigation";
import { createContext, useEffect, useState } from "react";

const defaultValue: UserContextType = {
  chain: CHAIN_TYPE.EVM,
  setchain: () => {},
  isUtxConnected: false,
  currentPrice: 0,
  isChainDisable: false,
  disableChain: () => {},
};

export const UserContext = createContext<UserContextType>(defaultValue);

export function Context(props: ContextProps) {
  const [chain, setchain] = useState<string>(CHAIN_TYPE.EVM);
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [currentPrice, setCurrentPrice] = useState(0);
  const pathname = usePathname();
  const [isChainDisable, disableChain] = useState<boolean>(false);

  // Access route information

  useEffect(() => {
    if (typeof window !== "undefined") {
      const urlObj = new URL(window.location.href);
      const queryParams = new URLSearchParams(urlObj.search);
      const chain = queryParams.get("chain");

      const storedChain =
        chain !== "EVM" && chain !== "UTXO"
          ? localStorage.getItem("chain")
          : chain;

      if (storedChain) {
        setchain(storedChain);
      }
      setIsMounted(true);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [pathname]);

  useEffect(() => {
    if (isMounted && typeof window !== "undefined") {
      localStorage.setItem("chain", chain);
    }
  }, [chain, isMounted]);

  // Function to save data to cache
  function saveToCache<T>(key: string, value: T): void {
    const timestamp = new Date().getTime();
    const data = {
      value,
      timestamp,
    };
    localStorage.setItem(key, JSON.stringify(data));
  }

  // Function to check if the cache is valid
  function isCacheValid(key: string): boolean {
    const data = localStorage.getItem(key);
    if (!data) return false;

    const parsedData = JSON.parse(data);
    const now = new Date().getTime();
    const oneMinute = 60000;

    return now - parsedData.timestamp < oneMinute;
  }

  // Function to get value from cache or call API if cache is invalid
  async function getValue<T>(
    key: string,
    apiCallFunction: () => Promise<T>
  ): Promise<T> {
    if (isCacheValid(key)) {
      const cachedData = localStorage.getItem(key);
      if (cachedData) {
        return JSON.parse(cachedData).value;
      }
    }
    const newValue = await apiCallFunction();
    saveToCache(key, newValue);
    return newValue;
  }

  const fetchDetails = async (): Promise<number | null> => {
    let response = await UseGetApi({
      url: process.env.NEXT_PUBLIC_CURRENT_PRICE,
    });

    if (!response?.success) {
      response = await axios({
        url: process.env.NEXT_PUBLIC_PRICE_API,
        headers: { project: process.env.NEXT_PUBLIC_BLOCKDAG_WEBSITE },
      });
      return response?.data?.tokenPrice;
    }

    return response?.data?.price;
  };

  // Function to fetch data using cache logic
  async function fetchData(): Promise<void> {
    const key = "currentPrice";
    const currentPrice: any = await getValue(key, fetchDetails);
    setCurrentPrice(currentPrice || 0);
  }

  // Call fetchData whenever you need the data

  return (
    <UserContext.Provider
      value={{
        chain,
        setchain,
        isUtxConnected: chain === CHAIN_TYPE.UTXO,
        currentPrice,
        isChainDisable,
        disableChain,
      }}
    >
      {props.children}
    </UserContext.Provider>
  );
}
